import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Modal, Select, Space } from "antd";
import HeaderComponent from "../component/header";
import {
    DemographicsData,
    GetDemographicResponse,
    GetDemographicResult,
} from "../constant";
import { useNavigate } from "react-router-dom";
import { api_updateDemographic } from "../api/UpdateFile";
import { api_getDemographic } from "../api/verify";
import { api_get_schools } from "../api/info";

const { Option } = Select;

type EditableFieldName =
    | "company"
    | "dba"
    | "School"
    | "practiceaddress1"
    | "practiceaddress2"
    | "practicestate"
    | "practicecity"
    | "practicezip"
    | "mailingaddress1"
    | "mailingaddress2"
    | "mailingcity"
    | "mailingzip"
    | "telephone"
    | "telephone2"
    | "fax"
    | "email"
    | "email2"
    | "web";

type EditableFieldConfig = {
    name: EditableFieldName;
    label: string;
    options?: "schools";
};

const editableFieldColumns: EditableFieldConfig[][] = [
    [
        { name: "company", label: "Legal Entity" },
        { name: "dba", label: "DBA" },
        { name: "School", label: "School", options: "schools" },
        { name: "practiceaddress1", label: "Practice Address1" },
        { name: "practiceaddress2", label: "Practice Address2" },
        { name: "practicestate", label: "Practice State" },
        { name: "practicecity", label: "Practice City" },
        { name: "practicezip", label: "Practice Zip" },
        { name: "mailingaddress1", label: "Mailing Address 1" },
        { name: "mailingaddress2", label: "Mailing Address 2" },
        { name: "mailingcity", label: "Mailing City" },
        { name: "mailingzip", label: "Mailing Zip" },
        { name: "telephone", label: "Telephone" },
        { name: "telephone2", label: "Telephone 2" },
        { name: "fax", label: "Telefax" },
        { name: "email", label: "Email" },
        { name: "email2", label: "Email 2" },
        { name: "web", label: "Web Site" }
    ],
];

export function isGetDemographicResponse(result: GetDemographicResult): result is GetDemographicResponse {
    return result.success === true;
}

const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<DemographicsData>();
    const [changeData, setChangeData] = useState<Partial<Record<EditableFieldName, string>>>({});
    const [schoolOptions, setSchoolOptions] = useState<string[]>([]);
    const [schoolLoadError, setSchoolLoadError] = useState<string>("");

    useEffect(() => {
        const token = localStorage.getItem("token");
        const storedFileId = localStorage.getItem("fileid");
        const fileid = storedFileId ? Number(storedFileId) : null;

        if (token === null || fileid === null || Number.isNaN(fileid)) {
            navigate("/login");
            return;
        }

        api_getDemographic(fileid)
            .then((response) => {
                if (response.success && isGetDemographicResponse(response)) {
                    setFormData(response.demographic);
                    setChangeData({});
                }
            })
            .catch(() => {
                return;
            });
    }, [navigate]);

    useEffect(() => {
        let isMounted = true;

        const loadSchools = async () => {
            setSchoolLoadError("");

            try {
                const data = await api_get_schools();
                if (!isMounted) {
                    return;
                }

                const names = Array.isArray(data)
                    ? data
                        .map((item) => String(item ?? "").trim())
                        .filter((item) => item.length > 0)
                    : [];

                const uniqueNames = Array.from(new Set(names));
                const ensuredUnknown = uniqueNames.includes("Unknown")
                    ? uniqueNames
                    : ["Unknown", ...uniqueNames];

                setSchoolOptions(ensuredUnknown);
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                // setSchoolOptions(["Unknown"]);
                setSchoolLoadError("Unable to load schools. Please try again.");
            }
        };

        loadSchools();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!formData || schoolOptions.length === 0) {
            return;
        }

        const currentSchool = String(formData.School ?? "").trim();
        if (currentSchool.length > 0 && schoolOptions.includes(currentSchool)) {
            return;
        }

        setFormData((previous) => {
            if (!previous || previous.School === "Unknown") {
                return previous;
            }

            return {
                ...previous,
                School: "Unknown",
            };
        });
    }, [formData, schoolOptions]);

    const updateDashboardLocalStorage = (current: DemographicsData | undefined, changes: Partial<Record<EditableFieldName, string>>) => {
        if (!current) {
            return;
        }

        const dashboardData = editableFieldColumns.flat().reduce((acc, field) => {
            const currentValue = String(current[field.name] ?? "");
            const changedValue = String(changes[field.name] ?? "");

            acc[field.name] = {
                current: currentValue,
                changed: changedValue,
            };

            return acc;
        }, {} as Record<EditableFieldName, { current: string; changed: string }>);

        localStorage.setItem("dashboard_data", JSON.stringify(dashboardData));
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate("/login");
    };

    const handleFieldChange = (fieldName: EditableFieldName, value: string) => {
        setChangeData((previous) => ({
            ...previous,
            [fieldName]: value,
        }));
    };

    const handleClickUpdate = () => {
        if (!formData) {
            return;
        }

        const currentSchool = String(formData.School ?? "").trim().toLowerCase();
        const changeSchool = String(changeData.School ?? "").trim().toLowerCase();
        if (currentSchool === "unknown" && (changeSchool === "" || changeSchool === "unknown")) {
            Modal.error({
                content: "Please select a valid school",
            });
            return;
        }

        const updateKeys = Object.keys(changeData) as EditableFieldName[];
        // if (updateKeys.length === 0) {
        //     message.warning("Please enter at least one change before updating.");
        //     return;
        // }

        const mergedData: DemographicsData = { ...formData };
        updateKeys.forEach((key) => {
            mergedData[key] = changeData[key] ?? "";
        });

        api_updateDemographic(mergedData)
        .then((response) => {
            if (response.bSuccess) {
                updateDashboardLocalStorage(formData, changeData);
                setFormData(mergedData);
                setChangeData({});
                message.success("Success to update the file info");
                navigate("/policynewal?fileid=" + mergedData.fileid);
            } else {
                message.error("Fail to update the file info");
            }
        })
        .catch((error) => {
            console.error("An error occurred:", error.message);
        });
    };

    // useEffect(() => {
    //     updateDashboardLocalStorage(formData, changeData);
    // }, [formData, changeData]);

    const renderCurrentValue = (field: EditableFieldConfig) => {
        const value = formData?.[field.name] ?? "";

        return (
            <div className="w-[400px] min-h-5 px-2 py-[1px] text-xs text-style border border-gray-200 rounded bg-gray-50 flex items-center overflow-hidden whitespace-nowrap text-ellipsis">
                {String(value || "-")}
            </div>
        );
    };

    const renderChangeInput = (field: EditableFieldConfig) => {
        if (field.name === "practicestate") {
            return (
                <div className="w-[400px] h-5 px-2 text-xs text-style flex items-center">
                    Please call our office to change practice state.
                </div>
            );
        }

        const value = changeData[field.name];

        if (field.options === "schools") {
            return (
                <div className="w-[400px]">
                    <Select
                        allowClear
                        value={value === undefined ? undefined : value}
                        placeholder="Enter change"
                        className="w-full h-5 custom-select"
                        disabled={Boolean(schoolLoadError)}
                        notFoundContent="No schools found"
                        onChange={(selectedValue) => handleFieldChange(field.name, selectedValue ?? "")}
                    >
                        {schoolOptions.map((option) => (
                            <Option key={option} value={option}>
                                {option}
                            </Option>
                        ))}
                    </Select>
                    {schoolLoadError && (
                        <div className="text-xs text-red-600 mt-1">{schoolLoadError}</div>
                    )}
                </div>
            );
        }

        return (
            <Input
                value={value ?? ""}
                placeholder="Enter change"
                className="w-[400px] h-5 text-xs text-style"
                onChange={(event) => handleFieldChange(field.name, event.target.value)}
            />
        );
    };

    return (
        <div>
            <HeaderComponent />
            <div className="body-height text-style">
                <div className="flex justify-between items-center mt-2 ml-2 mr-2">
                    <div className="text-style mt-8 ml-10 text-xs">
                        Please confirm this demographic information, please provide updates when something has changed.
                    </div>
                    <Button type="primary" className="w-24 h-6 mt-5 mr-10" onClick={handleLogout}>
                        Logout
                    </Button>
                </div>
                <div className="flex mt-10 justify-center">
                    <Form layout="horizontal">
                        <div className="flex flex-row mt-2 ml-2">
                            {editableFieldColumns.map((column, columnIndex) => (
                                <div key={columnIndex} className="flex flex-col">
                                    <Space direction="vertical" className="w-[1000px]">
                                        {column.map((field) => (
                                            <Form.Item
                                                key={field.name}
                                                label={<span className="text-xs">{field.label}</span>}
                                                labelAlign="left"
                                                labelCol={{ span: 3 }}
                                                wrapperCol={{ span: 21 }}
                                                style={{ marginBottom: "-6px", marginTop: "-6px", paddingTop: 0, paddingBottom: 0 }}
                                            >
                                                <div className="flex gap-2">
                                                    {renderCurrentValue(field)}
                                                    {renderChangeInput(field)}
                                                </div>
                                            </Form.Item>
                                        ))}
                                    </Space>
                                </div>
                            ))}
                        </div>
                    </Form>
                </div>

                <div className="flex justify-center mt-10">
                    <Button type="primary" className="w-32 h-10 mt-5 mr-10" onClick={handleClickUpdate}>
                        Continue
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;